/**
 * Ontvangstpunt voor de Web Vitals-beacons uit de browser.
 *
 * Bewust een publieke server-route (geen server-functie): `navigator.sendBeacon`
 * stuurt een kale POST zonder de RPC-serialisatie van TanStack.
 * Er wordt niets persoonlijks opgeslagen — geen IP, geen cookie, geen
 * identificatie die de paginaweergave overleeft.
 */
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { isAllowedOrigin } from "@/lib/contact-abuse.server";
import {
  MAX_VALUE,
  METRIC_NAMES,
  NAVIGATION_TYPES,
  VITALS_SITE_ID,
  deviceFromUserAgent,
  isLikelyBot,
  normalizeRoute,
  rateMetric,
} from "@/lib/web-vitals";

const metricSchema = z.object({
  name: z.enum(METRIC_NAMES),
  value: z.number().finite().min(0),
  route: z.string().min(1).max(400),
  navigationType: z.string().max(40).optional().default("navigate"),
  attribution: z.record(z.string(), z.unknown()).optional().default({}),
});

const beaconSchema = z.object({
  pageLoadId: z.string().uuid(),
  deviceType: z.enum(["mobile", "desktop"]).optional(),
  connectionType: z.string().max(20).nullable().optional(),
  metrics: z.array(metricSchema).min(1).max(40),
});

function corsFor(request: Request): Record<string, string> {
  const origin = request.headers.get("origin");
  const headers: Record<string, string> = {
    Vary: "Origin",
    "Access-Control-Allow-Methods": "POST, OPTIONS",
    "Access-Control-Allow-Headers": "Content-Type",
  };
  if (origin && isAllowedOrigin(origin, request.url))
    headers["Access-Control-Allow-Origin"] = origin;
  return headers;
}

export const Route = createFileRoute("/api/public/vitals")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      OPTIONS: async ({ request }) =>
        new Response(null, { status: 204, headers: corsFor(request) }),
      POST: async ({ request }) => {
        const CORS = corsFor(request);
        if (!isAllowedOrigin(request.headers.get("origin"), request.url)) {
          return new Response(null, { status: 204, headers: CORS });
        }

        const userAgent = request.headers.get("user-agent");
        if (isLikelyBot(userAgent)) return new Response(null, { status: 204, headers: CORS });

        let parsed;
        try {
          parsed = beaconSchema.parse(await request.json());
        } catch {
          return new Response(null, { status: 204, headers: CORS });
        }

        const device = parsed.deviceType ?? deviceFromUserAgent(userAgent);
        const country =
          request.headers.get("cf-ipcountry") ?? request.headers.get("x-vercel-ip-country") ?? null;

        const rows = parsed.metrics
          .filter((m) => m.value <= MAX_VALUE[m.name])
          .map((m) => ({
            site_id: VITALS_SITE_ID,
            page_load_id: parsed.pageLoadId,
            route: normalizeRoute(m.route),
            metric_name: m.name,
            value: m.value,
            rating: rateMetric(m.name, m.value),
            navigation_type: (NAVIGATION_TYPES as readonly string[]).includes(m.navigationType)
              ? m.navigationType
              : "navigate",
            device_type: device,
            connection_type: parsed.connectionType?.slice(0, 20) ?? null,
            country: country && country.length <= 3 ? country.toUpperCase() : null,
            attribution: (m.attribution ?? {}) as Record<string, never>,
          }));

        if (!rows.length) return new Response(null, { status: 204, headers: CORS });

        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { error } = await supabaseAdmin.from("web_vitals_metrics").insert(rows);
        if (error) console.error("[vitals] insert mislukt:", error.message);

        return new Response(null, { status: 204, headers: CORS });
      },
    },
  },
});
